import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { PriorityBadge } from '@/components/custom/PriorityBadge';

const PRIORITY_KEYS = ['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const;

interface BulkPriorityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onConfirm: (priority: string) => void;
}

export function BulkPriorityDialog({ open, onOpenChange, selectedCount, onConfirm }: BulkPriorityDialogProps) {
  const { t } = useTranslation(['tickets', 'common']);
  const [selected, setSelected] = useState<string | null>(null);

  function handleOpenChange(value: boolean) {
    if (!value) setSelected(null);
    onOpenChange(value);
  }

  function handleApply() {
    if (!selected) return;
    onConfirm(selected);
    setSelected(null);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('tickets:bulk.priorityTitle')}</DialogTitle>
          <DialogDescription>
            {t('tickets:bulk.priorityDesc', { count: selectedCount })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 py-2">
          {PRIORITY_KEYS.map((key) => (
            <button
              key={key}
              onClick={() => setSelected(key)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                selected === key
                  ? 'border-brand-rose bg-brand-rose/5'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <PriorityBadge priority={key} />
              <span className="text-sm font-medium text-slate-700">{t(`common:priority.${key}`)}</span>
            </button>
          ))}
        </div>

        <DialogFooter>
          <button
            onClick={() => handleOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            onClick={handleApply}
            disabled={!selected}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-brand-rose to-brand-orange hover:shadow-md"
          >
            {t('common:actions.apply')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
